import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Plus, Trash, PencilSimple, FileText, CheckCircle, XCircle, HourglassSimple, Receipt, Eye, Upload, X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptySupplier = { name: '', siret: '', contact_name: '', email: '', phone: '', address: '', category: '', notes: '' };

// ========== SUPPLIER FORM MODAL ==========
const SupplierModal = ({ supplier, onClose, onSaved }) => {
  const [form, setForm] = useState(supplier || emptySupplier);
  const [saving, setSaving] = useState(false);
  const isEdit = !!supplier?.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nom obligatoire'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/suppliers/${supplier.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/suppliers`, form, { withCredentials: true });
      }
      toast.success('Enregistré');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '520px', padding: '24px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0 }}>{isEdit ? 'Modifier' : 'Nouveau'} fournisseur</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Nom*</label><input data-testid="supplier-name" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div className="form-group"><label className="form-label">Catégorie</label><input className="form-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Matériaux, Location, ..." /></div>
            <div className="form-group"><label className="form-label">SIRET</label><input className="form-input" value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} /></div>
          </div>
          <div className="form-group"><label className="form-label">Contact</label><input className="form-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Téléphone</label><input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="form-group"><label className="form-label">Adresse</label><input className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Notes</label><textarea rows={2} className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="supplier-save">{saving ? '...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== QUOTE UPLOAD MODAL ==========
const QuoteModal = ({ supplier, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Titre obligatoire'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('amount_ht', String(amount || 0));
      fd.append('date', date);
      fd.append('status', status);
      fd.append('notes', notes);
      if (file) fd.append('file', file);
      await axios.post(`${API_URL}/api/suppliers/${supplier.id}/quotes`, fd, { withCredentials: true });
      toast.success('Devis enregistré');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '520px', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0 }}>Nouveau devis — {supplier.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Titre / référence*</label><input data-testid="quote-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} required /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div className="form-group"><label className="form-label">Montant HT</label><input type="number" step="0.01" className="form-input" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Date</label><input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label className="form-label">Avancement</label>
            <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="quote-status">
              <option value="pending">En attente</option>
              <option value="approved">Validé</option>
              <option value="rejected">Refusé</option>
            </select>
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea rows={2} className="form-input" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Fichier (PDF/Image)</label><input type="file" accept="application/pdf,image/*" className="form-input" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="quote-save">{saving ? '...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== INVOICE MODAL WITH OCR ==========
const InvoiceModal = ({ supplier, projects, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const analyze = async () => {
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/suppliers/${supplier.id}/invoices/ocr`, fd, { withCredentials: true });
      if (res.data.success) {
        setData(res.data);
        toast.success(`${res.data.lines.length} ligne(s) extraite(s)`);
      } else {
        toast.error(res.data.error || 'OCR échoué');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur OCR'); }
    finally { setAnalyzing(false); }
  };

  const updateLine = (idx, field, value) => {
    const lines = [...data.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    setData({ ...data, lines });
  };

  const removeLine = (idx) => {
    const lines = data.lines.filter((_, i) => i !== idx);
    setData({ ...data, lines });
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        tmp_filename: data.tmp_filename,
        original_filename: data.original_filename,
        numero_facture: data.numero_facture,
        date_facture: data.date_facture,
        total_ht_global: data.total_ht_global,
        total_ttc_global: data.total_ttc_global,
        lines: data.lines.map(l => ({
          description: l.description,
          quantity: Number(l.quantity) || 0,
          unit_price_ht: Number(l.unit_price_ht) || 0,
          total_ht: Number(l.total_ht) || 0,
          tva_rate: Number(l.tva_rate) || 20,
          project_id: l.project_id || '',
        })),
      };
      const res = await axios.post(`${API_URL}/api/suppliers/${supplier.id}/invoices`, payload, { withCredentials: true });
      toast.success(`Facture enregistrée (${res.data.expenses_created} dépense(s) créée(s))`);
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '10px', width: '100%', maxWidth: '980px', padding: '24px', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0 }}>Importer une facture — {supplier.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>

        {!data ? (
          <div>
            <p style={{ color: '#5E5F66', fontSize: '0.9rem', marginBottom: '12px' }}>
              Importez un PDF ou une photo de la facture. Gemini analysera automatiquement les lignes, que vous pourrez ensuite affecter chantier par chantier.
            </p>
            <div className="form-group">
              <input ref={inputRef} type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="invoice-file-input" />
              {file && <div style={{ fontSize: '0.85rem', color: '#5E5F66', marginTop: '6px' }}>Fichier: {file.name} ({Math.round(file.size / 1024)} Ko)</div>}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button className="btn btn-primary" onClick={analyze} disabled={!file || analyzing} data-testid="invoice-analyze-btn">
                {analyzing ? 'Analyse IA en cours...' : 'Analyser avec Gemini'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px', padding: '12px', background: '#F4F5F7', borderRadius: '8px' }}>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label" style={{ fontSize: '0.72rem' }}>N° facture</label><input className="form-input" value={data.numero_facture} onChange={(e) => setData({ ...data, numero_facture: e.target.value })} /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label" style={{ fontSize: '0.72rem' }}>Date</label><input type="date" className="form-input" value={data.date_facture} onChange={(e) => setData({ ...data, date_facture: e.target.value })} /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label" style={{ fontSize: '0.72rem' }}>Total HT</label><input type="number" step="0.01" className="form-input" value={data.total_ht_global} onChange={(e) => setData({ ...data, total_ht_global: Number(e.target.value) })} /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label" style={{ fontSize: '0.72rem' }}>Total TTC</label><input type="number" step="0.01" className="form-input" value={data.total_ttc_global} onChange={(e) => setData({ ...data, total_ttc_global: Number(e.target.value) })} /></div>
            </div>

            <h4 style={{ margin: '0 0 10px' }}>Lignes détectées ({data.lines.length})</h4>
            <div style={{ overflow: 'auto', maxHeight: '45vh' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style={{ width: '70px' }}>Qté</th>
                    <th style={{ width: '90px' }}>PU HT</th>
                    <th style={{ width: '90px' }}>Total HT</th>
                    <th style={{ width: '70px' }}>TVA %</th>
                    <th style={{ minWidth: '180px' }}>Chantier</th>
                    <th style={{ width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((l, idx) => (
                    <tr key={idx}>
                      <td><input className="form-input" value={l.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} style={{ minWidth: '240px' }} /></td>
                      <td><input type="number" step="1" className="form-input" value={l.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} /></td>
                      <td><input type="number" step="0.01" className="form-input" value={l.unit_price_ht} onChange={(e) => updateLine(idx, 'unit_price_ht', e.target.value)} /></td>
                      <td><input type="number" step="0.01" className="form-input" value={l.total_ht} onChange={(e) => updateLine(idx, 'total_ht', e.target.value)} /></td>
                      <td><input type="number" step="0.1" className="form-input" value={l.tva_rate} onChange={(e) => updateLine(idx, 'tva_rate', e.target.value)} /></td>
                      <td>
                        <select className="form-input" value={l.project_id || ''} onChange={(e) => updateLine(idx, 'project_id', e.target.value)} data-testid={`invoice-line-project-${idx}`}>
                          <option value="">-- Non affectée --</option>
                          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td><button onClick={() => removeLine(idx)} className="btn btn-danger btn-sm btn-icon"><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '14px', padding: '10px', background: '#FFF8E1', borderRadius: '6px', fontSize: '0.82rem', color: '#5E5F66' }}>
              <strong>Astuce:</strong> chaque ligne affectée à un chantier créera automatiquement une dépense dans le chantier correspondant.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '14px' }}>
              <button className="btn btn-secondary" onClick={() => { setData(null); setFile(null); }}>← Recommencer</button>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
                <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="invoice-save-btn">{saving ? '...' : 'Enregistrer la facture'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== DETAIL PANEL (quotes + invoices) ==========
const SupplierDetail = ({ supplier, onBack, projects, onChanged }) => {
  const [activeTab, setActiveTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showQuote, setShowQuote] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [qRes, iRes] = await Promise.all([
        axios.get(`${API_URL}/api/suppliers/${supplier.id}/quotes`, { withCredentials: true }),
        axios.get(`${API_URL}/api/suppliers/${supplier.id}/invoices`, { withCredentials: true }),
      ]);
      setQuotes(qRes.data || []);
      setInvoices(iRes.data || []);
    } catch { toast.error('Erreur chargement'); }
  }, [supplier.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setQuoteStatus = async (quoteId, status) => {
    try {
      const fd = new FormData();
      fd.append('status', status);
      await axios.put(`${API_URL}/api/suppliers/quotes/${quoteId}/status`, fd, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await axios.delete(`${API_URL}/api/suppliers/quotes/${quoteId}`, { withCredentials: true });
      fetchAll();
      onChanged && onChanged();
    } catch { toast.error('Erreur'); }
  };

  const deleteInvoice = async (invId) => {
    if (!window.confirm('Supprimer cette facture et ses dépenses liées ?')) return;
    try {
      await axios.delete(`${API_URL}/api/suppliers/invoices/${invId}`, { withCredentials: true });
      fetchAll();
      onChanged && onChanged();
    } catch { toast.error('Erreur'); }
  };

  const badge = (status) => {
    const map = {
      pending: { bg: '#FFF8E1', color: '#EF6C00', label: 'En attente', icon: <HourglassSimple size={12} /> },
      approved: { bg: '#E8F5E9', color: '#2E7D32', label: 'Validé', icon: <CheckCircle size={12} /> },
      rejected: { bg: '#FFEBEE', color: '#C62828', label: 'Refusé', icon: <XCircle size={12} /> },
    };
    const s = map[status] || map.pending;
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '10px', background: s.bg, color: s.color, fontSize: '0.72rem', fontWeight: '700' }}>{s.icon} {s.label}</span>;
  };

  return (
    <div>
      <button className="btn btn-secondary" onClick={onBack} style={{ marginBottom: '14px' }}>← Retour</button>
      <div className="card" style={{ padding: '18px 22px', marginBottom: '14px', borderLeft: '4px solid #002FA7' }}>
        <h2 style={{ margin: '0 0 4px' }}>{supplier.name}</h2>
        <div style={{ fontSize: '0.88rem', color: '#5E5F66', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          {supplier.category && <span><strong>Catégorie:</strong> {supplier.category}</span>}
          {supplier.contact_name && <span><strong>Contact:</strong> {supplier.contact_name}</span>}
          {supplier.phone && <span><strong>Tél:</strong> {supplier.phone}</span>}
          {supplier.email && <span><strong>Email:</strong> {supplier.email}</span>}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: '14px' }}>
        <button className={`tab ${activeTab === 'quotes' ? 'active' : ''}`} onClick={() => setActiveTab('quotes')} data-testid="supplier-tab-quotes">Devis ({quotes.length})</button>
        <button className={`tab ${activeTab === 'invoices' ? 'active' : ''}`} onClick={() => setActiveTab('invoices')} data-testid="supplier-tab-invoices">Factures ({invoices.length})</button>
      </div>

      {activeTab === 'quotes' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowQuote(true)} data-testid="add-quote-btn"><Plus size={14} /> Ajouter un devis</button>
          </div>
          {quotes.length === 0 ? (
            <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Aucun devis</div>
          ) : (
            <div className="card" style={{ overflow: 'auto' }}>
              <table className="data-table">
                <thead><tr><th>Titre</th><th>Date</th><th style={{ textAlign: 'right' }}>Montant HT</th><th>Statut</th><th>Fichier</th><th></th></tr></thead>
                <tbody>
                  {quotes.map(q => (
                    <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                      <td style={{ fontWeight: '600' }}>{q.title}</td>
                      <td>{q.date || '-'}</td>
                      <td style={{ textAlign: 'right' }}>{(q.amount_ht || 0).toLocaleString('fr-FR')} €</td>
                      <td>
                        <select value={q.status} onChange={(e) => setQuoteStatus(q.id, e.target.value)} style={{ padding: '3px 6px', border: '1px solid #E2E2E6', borderRadius: '6px', fontSize: '0.8rem' }} data-testid={`quote-status-${q.id}`}>
                          <option value="pending">En attente</option>
                          <option value="approved">Validé</option>
                          <option value="rejected">Refusé</option>
                        </select>
                        <div style={{ marginTop: '4px' }}>{badge(q.status)}</div>
                      </td>
                      <td>{q.file_url ? <a href={`${API_URL}${q.file_url}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><Eye size={14} /> Voir</a> : '-'}</td>
                      <td><button className="btn btn-danger btn-sm btn-icon" onClick={() => deleteQuote(q.id)}><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'invoices' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowInvoice(true)} data-testid="add-invoice-btn"><Upload size={14} /> Importer une facture</button>
          </div>
          {invoices.length === 0 ? (
            <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Aucune facture</div>
          ) : (
            <div className="card" style={{ overflow: 'auto' }}>
              <table className="data-table">
                <thead><tr><th>N° facture</th><th>Date</th><th>Lignes</th><th style={{ textAlign: 'right' }}>Total HT</th><th style={{ textAlign: 'right' }}>Total TTC</th><th>Fichier</th><th></th></tr></thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <td style={{ fontWeight: '600' }}>{inv.numero_facture || '-'}</td>
                      <td>{inv.date_facture || '-'}</td>
                      <td>{(inv.lines || []).length}</td>
                      <td style={{ textAlign: 'right' }}>{(inv.total_ht_global || 0).toLocaleString('fr-FR')} €</td>
                      <td style={{ textAlign: 'right' }}>{(inv.total_ttc_global || 0).toLocaleString('fr-FR')} €</td>
                      <td>{inv.file_url ? <a href={`${API_URL}${inv.file_url}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><Eye size={14} /> Voir</a> : '-'}</td>
                      <td><button className="btn btn-danger btn-sm btn-icon" onClick={() => deleteInvoice(inv.id)}><Trash size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showQuote && <QuoteModal supplier={supplier} onClose={() => setShowQuote(false)} onSaved={() => { fetchAll(); onChanged && onChanged(); }} />}
      {showInvoice && <InvoiceModal supplier={supplier} projects={projects} onClose={() => setShowInvoice(false)} onSaved={() => { fetchAll(); onChanged && onChanged(); }} />}
    </div>
  );
};

// ========== MAIN PAGE ==========
const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/api/suppliers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
      ]);
      setSuppliers(sRes.data || []);
      setProjects(pRes.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur et tous ses devis/factures ?')) return;
    try {
      await axios.delete(`${API_URL}/api/suppliers/${id}`, { withCredentials: true });
      toast.success('Supprimé');
      fetchAll();
      if (selected?.id === id) setSelected(null);
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="suppliers-page">
      {selected ? (
        <SupplierDetail supplier={selected} projects={projects} onBack={() => setSelected(null)} onChanged={fetchAll} />
      ) : (
        <>
          <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Truck size={28} weight="duotone" /> Fournisseurs
              </h1>
              <p className="page-subtitle">Référencez vos fournisseurs, leurs devis et importez leurs factures avec OCR IA.</p>
            </div>
            <button className="btn btn-primary" onClick={() => { setEditItem(null); setShowModal(true); }} data-testid="add-supplier-btn">
              <Plus size={16} /> Nouveau fournisseur
            </button>
          </div>

          {suppliers.length === 0 ? (
            <div className="card" style={{ padding: '48px', textAlign: 'center', color: '#5E5F66' }}>
              <Truck size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
              <p style={{ margin: 0, fontWeight: '500' }}>Aucun fournisseur</p>
              <p style={{ fontSize: '0.86rem', marginTop: '4px' }}>Cliquez sur "Nouveau fournisseur" pour commencer.</p>
            </div>
          ) : (
            <div className="card" style={{ overflow: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr><th>Nom</th><th>Catégorie</th><th>Contact</th><th>Email</th><th>Téléphone</th><th style={{ textAlign: 'center' }}>Devis</th><th style={{ textAlign: 'center' }}>Factures</th><th></th></tr>
                </thead>
                <tbody>
                  {suppliers.map(s => (
                    <tr key={s.id} data-testid={`supplier-row-${s.id}`} style={{ cursor: 'pointer' }} onClick={() => setSelected(s)}>
                      <td style={{ fontWeight: '700' }}>{s.name}</td>
                      <td>{s.category || '-'}</td>
                      <td>{s.contact_name || '-'}</td>
                      <td>{s.email || '-'}</td>
                      <td>{s.phone || '-'}</td>
                      <td style={{ textAlign: 'center' }}><span style={{ padding: '2px 8px', background: '#E3F2FD', color: '#1565C0', borderRadius: '10px', fontWeight: '700', fontSize: '0.8rem' }}>{s.quotes_count}</span></td>
                      <td style={{ textAlign: 'center' }}><span style={{ padding: '2px 8px', background: '#E8F5E9', color: '#2E7D32', borderRadius: '10px', fontWeight: '700', fontSize: '0.8rem' }}>{s.invoices_count}</span></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditItem(s); setShowModal(true); }} data-testid={`edit-supplier-${s.id}`}><PencilSimple size={14} /></button>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(s.id)}><Trash size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showModal && <SupplierModal supplier={editItem} onClose={() => { setShowModal(false); setEditItem(null); }} onSaved={fetchAll} />}
    </div>
  );
};

export default SuppliersPage;
